/**
 * METRICS PANEL
 * =============
 * Panel de métricas en tiempo real para sesiones de coaching IA.
 * Muestra estado de la máquina de estados, fricción, intervenciones y progreso.
 * 
 * Funcionalidades:
 * - Barra de progreso de fases (State Machine)
 * - Indicador de nivel de fricción (low/medium/high)
 * - Contador de errores críticos y warnings
 * - Badges de intervenciones pendientes
 * - Intent detectado en última interacción
 * - Métricas de sesión (turnos, tiempo)
 * 
 * Props:
 * - sessionMetrics: Objeto con metadata del backend
 * - currentPhase: Fase actual de la state machine
 * - phaseHistory: Historial de fases completadas
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { getSafeToken } from '../../../utils/tokenHelper';
import { VERTEX_API_BASE } from '../../../utils/vertexApiHelper';
import { 
  TrendingUp, 
  AlertTriangle, 
  Zap,
  Clock,
  Target,
  Activity,
  BarChart3,
  FileText,
  Search,
  Download,
  X,
  Copy,
  CheckCheck,
  Settings,
  Trash2,
  Terminal,
  Package,
  ChevronDown
} from 'lucide-react';

const MetricsPanel = ({ sessionMetrics, currentPhase, currentPhaseDisplayName, phaseHistory, agentPhases = [], agentId, currentSessionId, scenarioInfo }) => {
  const { user } = useAuth();
  const isAdmin = user?.email === 'davidprudencio@hotmail.com';
  
  // Estado para colapsar/expandir descripción del escenario
  const [scenarioExpanded, setScenarioExpanded] = useState(false);
  
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [activeLogView, setActiveLogView] = useState(null);
  const [logData, setLogData] = useState(null);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Datos por defecto si no hay métricas
  const friction = sessionMetrics?.friction || { currentLevel: 'low', enabled: false };
  const interventions = sessionMetrics?.interventions || { total: 0, critical: 0, pending: 0 };
  const detectedIntent = sessionMetrics?.detectedIntent || null;
  // Turnos UI: displayTurns = completedTurns ?? aiTurnCount ?? 0
  const displayTurns =
    typeof sessionMetrics?.completedTurns === 'number' ? sessionMetrics.completedTurns :
    typeof sessionMetrics?.aiTurnCount === 'number' ? sessionMetrics.aiTurnCount :
    0;
  const tokenUsage = sessionMetrics?.tokenUsage || null;
  
  // 🐛 DEBUG: Ver qué llega a tokenUsage
  useEffect(() => {
    console.log('📊 [MetricsPanel DEBUG] tokenUsage:', tokenUsage);
    console.log('📊 [MetricsPanel DEBUG] sessionMetrics completo:', sessionMetrics);
  }, [tokenUsage, sessionMetrics]);
  
  // Usar fases del agente o fases por defecto
  const defaultPhases = [
    { id: 'WARMUP', label: 'Warmup', icon: '🤝' },
    { id: 'SCENARIO_SETUP', label: 'Escenario', icon: '📋' },
    { id: 'NEGOTIATION', label: 'Negociación', icon: '💼' },
    { id: 'PRESSURE_TEST', label: 'Presión', icon: '🔥' },
    { id: 'REFLECTION', label: 'Reflexión', icon: '💭' },
    { id: 'CLOSURE', label: 'Cierre', icon: '✅' }
  ];
  

  // 🎫 TDZ-FIX: Protección contra agentPhases null/undefined
  const agentPhasesSafe = Array.isArray(agentPhases) ? agentPhases : [];

  // Convertir fases del agente al formato esperado
  const allPhases = agentPhasesSafe.length > 0 
    ? agentPhasesSafe.map(phase => ({
        id: phase.phaseId || phase.id,  // phaseId del backend, fallback a id
        label: phase.displayName || phase.phaseId || phase.id,
        icon: phase.icon || '⭕'
      }))
    : defaultPhases;

  // 🎫 V4-P0-006: Normalización de IDs y construcción de sets (fuente de verdad)
  const currentPhaseId = typeof currentPhase === 'object' ? currentPhase?.id : currentPhase;
  
  // Set de fases completadas (exitedAt != null)
  const completedPhaseIds = new Set(
    (phaseHistory || [])
      .filter(p => p?.exitedAt != null)
      .map(p => p?.phaseId)
      .filter(Boolean)
  );
  
  // Set de fases visitadas (enteredAt != null)
  const visitedPhaseIds = new Set(
    (phaseHistory || [])
      .map(p => p?.phaseId)
      .filter(Boolean)
  );

  // ✅ Matching robusto: SOLO phaseId (NO usar label/displayName para lógica)
  const currentPhaseIndex = allPhases.findIndex((phase) => {
    const phaseId = phase?.phaseId || phase?.id;
    return phaseId && currentPhaseId && phaseId === currentPhaseId;
  });

  // 🧩 Debug logging (feature-flagged)
  const __V4_DEBUG__ =
    (typeof window !== 'undefined' && window.__VERTEX_DEBUG__ === true) ||
    (typeof window !== 'undefined' && window.__V4_DEBUG_PHASE_UI__ === true) ||
    false;

  if (__V4_DEBUG__) {
    console.debug('[V4-P0-006] Phase matching debug', {
      currentPhaseId,
      currentPhaseIndex,
      completedPhaseIds: Array.from(completedPhaseIds),
      visitedPhaseIds: Array.from(visitedPhaseIds),
      agentPhasesCount: allPhases.length,
      phaseHistoryCount: phaseHistory?.length || 0,
      allPhases: (allPhases || []).map((p) => ({
        id: p?.id,
        phaseId: p?.phaseId,
        label: p?.label,
      })),
    });
    
    // Warning: agentPhases vacío
    if (allPhases.length === 0) {
      console.warn('[V4-P0-006] agentPhases está vacío - usando fases por defecto');
    }
    
    // Warning: phaseIds en history que no existen en agentPhases
    const agentPhaseIds = new Set(allPhases.map(p => p?.phaseId || p?.id).filter(Boolean));
    const orphanPhaseIds = Array.from(visitedPhaseIds).filter(id => !agentPhaseIds.has(id));
    if (orphanPhaseIds.length > 0) {
      console.warn('[V4-P0-006] phaseIds en history sin definición en agentPhases:', orphanPhaseIds);
    }
    
    // Warning: currentPhaseId no existe en agentPhases
    if (currentPhaseId && currentPhaseIndex === -1) {
      console.warn('[V4-P0-006] currentPhaseId no encontrado en agentPhases', {
        currentPhaseId,
        availablePhaseIds: Array.from(agentPhaseIds),
      });
    }
  }
  
  // � VOCABULARIO CONGELADO: Nombres canónicos ÚNICOS (no usar variantes)
  // ✅ USAR: turnCount, maxTurns, progressPct, phaseHistoryLen
  // ❌ NO USAR: currentTurnCount, completedTurns, aiTurnCount, maxTurnsCount, progressPercentage
  const turnCount = sessionMetrics?.completedTurns ?? sessionMetrics?.aiTurnCount ?? sessionMetrics?.currentTurn ?? 0;
  const maxTurns = sessionMetrics?.maxTurns ?? 0;
  const progressPct = maxTurns > 0 ? Math.round((turnCount / maxTurns) * 100) : 0;
  const phaseHistoryLen = phaseHistory?.length ?? 0;

  // Auditoría de props críticos (solo una vez por render)
  React.useEffect(() => {
    console.log('[METRICS_PROPS]', {
      agentPhasesLen: agentPhasesSafe.length,
      phaseHistoryLen,
      currentPhaseId,
      turnCount,
      maxTurns,
      progressPct
    });
  }, [agentPhasesSafe.length, phaseHistoryLen, currentPhaseId, turnCount, maxTurns, progressPct]);
  
  // 🔍 DEBUG: Ver qué datos llegan del backend
  console.log('[MetricsPanel V4] sessionMetrics:', {
    turnCount,
    maxTurns,
    progressPct,
    phaseHistoryLen,
    currentPhaseId
  });
  
  // Datos de fases (de State Machine V4)
  const totalPhases = sessionMetrics?.totalPhases ?? allPhases.length;
  const phasesCompleted = phaseHistory?.length ?? sessionMetrics?.phasesCompleted ?? Math.max(0, currentPhaseIndex);

  // Colores según nivel de fricción
  const frictionColors = {
    low: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: '😊' },
    medium: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: '😐' },
    high: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: '😰' }
  };

  const frictionStyle = frictionColors[friction.currentLevel] || frictionColors.low;

  // Función para limpiar logs del servidor
  const handleClearLogs = async () => {
    if (!window.confirm('⚠️ ¿Estás seguro de borrar TODOS los logs del servidor?\n\nEsta acción limpiará el archivo vertex_server.log para que puedas iniciar una sesión de depuración limpia.')) return;
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${VERTEX_API_BASE}/v4/logs`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        alert('✅ Logs limpiados correctamente. Ahora puedes iniciar tu sesión.');
      } else {
        alert('❌ Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error limpiando logs:', error);
      alert('❌ Error de conexión al limpiar logs');
    }
  };

  // Función para obtener datos de logs
  const fetchLogData = async (endpoint, type) => {
    setIsLoadingLogs(true);
    setActiveLogView(type);
    setShowLogsModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${VERTEX_API_BASE}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setLogData(data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      setLogData({ error: error.message });
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // Función para copiar JSON al portapapeles
  const copyToClipboard = () => {
    const jsonString = JSON.stringify(logData, null, 2);
    navigator.clipboard.writeText(jsonString).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-3">
      
      {/* Descripción del Caso - Diseño minimalista */}
      {scenarioInfo && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg overflow-hidden">
          {/* Header colapsable */}
          <button
            onClick={() => setScenarioExpanded(!scenarioExpanded)}
            className="w-full flex items-start justify-between text-left p-3 hover:bg-blue-100/40 transition-colors"
          >
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <FileText className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
              <span className="text-xs font-semibold text-blue-900 leading-snug">
                {scenarioInfo.title}
              </span>
            </div>
            <ChevronDown 
              className={`w-4 h-4 text-blue-600 flex-shrink-0 transition-transform mt-0.5 ${
                scenarioExpanded ? 'rotate-180' : ''
              }`} 
            />
          </button>
          
          {/* Descripción expandible */}
          {scenarioExpanded && (
            <div className="px-3 pb-3 pt-1">
              <p className="text-[11px] text-gray-700 leading-relaxed whitespace-pre-wrap">
                {scenarioInfo.description}
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Panel de Métricas */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 pb-3">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Activity className="w-4 h-4 text-indigo-600" />
          Métricas de Sesión
        </h3>
        <span className="text-xs text-gray-500">
          <Clock className="w-3 h-3 inline mr-1" />
          En vivo
        </span>
      </div>

      {/* Progreso General - ELIMINADO: Redundante con SessionStatusBadge (V4-P0-007) */}

      {/* Lista Vertical de Fases - ELIMINADO: Redundante con SessionStatusBadge (V4-P0-007) */}

      {/* Métricas Clave */}
      <div className="space-y-2 border-t border-gray-200 pt-4">
        <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5 text-gray-600" />
          Métricas Clave
        </h4>

      {/* Fricción Adaptativa */}
      {friction.enabled && (
        <div className={`
          border rounded-lg p-3 ${frictionStyle.bg} ${frictionStyle.border}
        `}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-700">Nivel de Presión</span>
            <span className="text-lg">{frictionStyle.icon}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Zap className={`w-4 h-4 ${frictionStyle.text}`} />
            <span className={`text-sm font-semibold uppercase ${frictionStyle.text}`}>
              {friction.currentLevel === 'low' ? 'Bajo' : 
               friction.currentLevel === 'medium' ? 'Medio' : 'Alto'}
            </span>
          </div>

          {friction.escalationCount > 0 && (
            <p className="text-xs text-gray-600 mt-2">
              Escalaciones: {friction.escalationCount}
            </p>
          )}
        </div>
      )}

      {/* Intent Detectado */}
      {detectedIntent && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-purple-600" />
            <span className="text-xs font-medium text-gray-700">Intención Detectada</span>
          </div>
          <p className="text-sm font-semibold text-purple-900">
            {detectedIntent.intent?.replace(/_/g, ' ') || 'Analizando...'}
          </p>
          {detectedIntent.confidence && (
            <p className="text-xs text-purple-600 mt-1">
              Confianza: {Math.round(detectedIntent.confidence * 100)}%
            </p>
          )}
        </div>
      )}

      {/* Intervenciones */}
      {interventions.total > 0 && (
        <div className="space-y-2">
          <span className="text-xs font-medium text-gray-700">Intervenciones</span>
          
          <div className="grid grid-cols-2 gap-2">
            {/* Críticas */}
            {interventions.critical > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                <div className="flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                  <span className="text-xs font-semibold text-red-900">
                    {interventions.critical}
                  </span>
                </div>
                <p className="text-[10px] text-red-600 mt-0.5">Críticas</p>
              </div>
            )}

            {/* Pendientes */}
            {interventions.pending > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-amber-600" />
                  <span className="text-xs font-semibold text-amber-900">
                    {interventions.pending}
                  </span>
                </div>
                <p className="text-[10px] text-amber-600 mt-0.5">Pendientes</p>
              </div>
            )}

            {/* Total */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
              <div className="flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-gray-600" />
                <span className="text-xs font-semibold text-gray-900">
                  {interventions.total}
                </span>
              </div>
              <p className="text-[10px] text-gray-600 mt-0.5">Total</p>
            </div>
          </div>
        </div>
      )}

        {/* Turnos de conversación - ELIMINADO: Redundante con SessionStatusBadge (V4-P0-007) */}

        {/* Token Usage - Acumulado por IA (SOLO ADMIN) */}
        {isAdmin && tokenUsage && (
          <div className="space-y-2 mt-3">
            <h5 className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">
              Consumo de Tokens (Acumulado)
            </h5>
            
            {/* Cliente IA */}
            {tokenUsage.clienteIA && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-medium text-blue-700">
                    TKN IA_Client
                  </span>
                  <span className="text-xs font-bold text-blue-900">
                    {tokenUsage.clienteIA.totalTokens?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="text-[9px] text-blue-600 flex justify-between">
                  <span>Input: {tokenUsage.clienteIA.totalPrompt?.toLocaleString() || 0}</span>
                  <span>Output: {tokenUsage.clienteIA.totalCompletion?.toLocaleString() || 0}</span>
                </div>
                {/* Lista de turnos */}
                {tokenUsage.clienteIA.perTurn && tokenUsage.clienteIA.perTurn.length > 0 && (
                  <div className="mt-2 max-h-24 overflow-y-auto space-y-1">
                    {tokenUsage.clienteIA.perTurn.map((turn, idx) => (
                      <div key={idx} className="text-[9px] text-blue-700 flex justify-between bg-white/50 px-1.5 py-0.5 rounded">
                        <span>T{turn.turn}</span>
                        <span>{turn.total?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Moderador */}
            {tokenUsage.moderador && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-medium text-amber-700">
                    TKN Moderator
                  </span>
                  <span className="text-xs font-bold text-amber-900">
                    {tokenUsage.moderador.totalTokens?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="text-[9px] text-amber-600 flex justify-between">
                  <span>Input: {tokenUsage.moderador.totalPrompt?.toLocaleString() || 0}</span>
                  <span>Output: {tokenUsage.moderador.totalCompletion?.toLocaleString() || 0}</span>
                </div>
                {/* Lista de evaluaciones */}
                {tokenUsage.moderador.evaluations && tokenUsage.moderador.evaluations.length > 0 && (
                  <div className="mt-2 max-h-24 overflow-y-auto space-y-1">
                    {tokenUsage.moderador.evaluations.map((evaluation, idx) => (
                      <div key={idx} className="text-[9px] text-amber-700 flex justify-between bg-white/50 px-1.5 py-0.5 rounded">
                        <span>T{evaluation.turn}</span>
                        <span>{evaluation.total?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Coach */}
            {tokenUsage.coach && tokenUsage.coach.totalTokens > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-medium text-green-700">
                    TKN Coach
                  </span>
                  <span className="text-xs font-bold text-green-900">
                    {tokenUsage.coach.totalTokens?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="text-[9px] text-green-600 flex justify-between">
                  <span>Input: {tokenUsage.coach.totalPrompt?.toLocaleString() || 0}</span>
                  <span>Output: {tokenUsage.coach.totalCompletion?.toLocaleString() || 0}</span>
                </div>
                {/* Lista de solicitudes de ayuda */}
                {tokenUsage.coach.helpRequests && tokenUsage.coach.helpRequests.length > 0 && (
                  <div className="mt-2 max-h-24 overflow-y-auto space-y-1">
                    {tokenUsage.coach.helpRequests.map((help, idx) => (
                      <div key={idx} className="text-[9px] text-green-700 flex justify-between bg-white/50 px-1.5 py-0.5 rounded">
                        <span>T{help.turn}</span>
                        <span>{help.total?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Botones de Logs de Interacción - SOLO ADMIN */}
      {isAdmin && (
        <div className="border-t border-gray-200 pt-4 space-y-3">
          <h4 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5 text-gray-600" />
            Análisis de Interacciones
          </h4>
        
        {/* Grid de 3 columnas, 2 filas (6 botones funcionales) */}
        <div className="grid grid-cols-3 gap-2">
          {/* Estadísticas generales */}
          <button
            onClick={() => fetchLogData('/v4/logs/stats', 'stats')}
            className="flex flex-col items-center gap-1.5 p-2.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors group"
            title="Ver estadísticas generales de todas las interacciones"
          >
            <BarChart3 className="w-4 h-4 text-blue-600 group-hover:scale-110 transition-transform" />
            <span className="text-[9px] font-medium text-blue-900 text-center leading-tight">
              Stats
            </span>
          </button>

          {/* Logs individuales */}
          <button
            onClick={() => fetchLogData('/v4/logs/my-interactions?limit=50', 'interactions')}
            className="flex flex-col items-center gap-1.5 p-2.5 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors group"
            title="Ver logs individuales de interacciones"
          >
            <FileText className="w-4 h-4 text-purple-600 group-hover:scale-110 transition-transform" />
            <span className="text-[9px] font-medium text-purple-900 text-center leading-tight">
              Logs
            </span>
          </button>

          {/* Análisis de sesión */}
          <button
            onClick={() => {
              if (currentSessionId) {
                fetchLogData(`/v4/logs/session-analysis/${currentSessionId}`, 'session');
              } else {
                alert('No hay sesión activa');
              }
            }}
            disabled={!currentSessionId}
            className="flex flex-col items-center gap-1.5 p-2.5 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors group disabled:opacity-40 disabled:cursor-not-allowed"
            title="Análisis de la sesión actual"
          >
            <Search className="w-4 h-4 text-green-600 group-hover:scale-110 transition-transform" />
            <span className="text-[9px] font-medium text-green-900 text-center leading-tight">
              Sesión
            </span>
          </button>

          {/* Configuración del Agente (NUEVO) */}
          <button
            onClick={() => {
              if (agentId) {
                fetchLogData(`/v4/logs/agent-config/${agentId}`, 'agentConfig');
              } else {
                alert('No hay agente disponible');
              }
            }}
            disabled={!agentId}
            className="flex flex-col items-center gap-1.5 p-2.5 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg transition-colors group disabled:opacity-40 disabled:cursor-not-allowed"
            title="Descargar configuración completa del agente para análisis con IA"
          >
            <Settings className="w-4 h-4 text-teal-600 group-hover:scale-110 transition-transform" />
            <span className="text-[9px] font-medium text-teal-900 text-center leading-tight">
              Config
            </span>
          </button>

          {/* Limpiar Logs (NUEVO) */}
          <button
            onClick={handleClearLogs}
            className="flex flex-col items-center gap-1.5 p-2.5 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors group"
            title="Borrar todos los logs del servidor para iniciar limpio"
          >
            <Trash2 className="w-4 h-4 text-red-600 group-hover:scale-110 transition-transform" />
            <span className="text-[9px] font-medium text-red-900 text-center leading-tight">
              Limpiar
            </span>
          </button>

          {/* Server Log (NUEVO) */}
          <button
            onClick={() => {
              console.log('[DEBUG] Botón Log del Servidor clickeado');
              fetchLogData('/v4/logs', 'serverLog');
            }}
            className="flex flex-col items-center gap-1.5 p-2.5 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-lg transition-colors group"
            title="Ver log crudo del servidor (Full Prompt Debug)"
          >
            <Terminal className="w-4 h-4 text-white group-hover:scale-110 transition-transform" />
            <span className="text-[9px] font-medium text-white text-center leading-tight">
              Debug
            </span>
          </button>

        </div>

        <p className="text-[9px] text-gray-500 text-center mt-2">
          💡 Datos completos para análisis con IA externa
        </p>
        </div>
      )}

      {/* Modal de Logs */}
      {showLogsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header del Modal */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                {activeLogView === 'stats' && (
                  <>
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    Estadísticas Generales
                  </>
                )}
                {activeLogView === 'interactions' && (
                  <>
                    <FileText className="w-5 h-5 text-purple-600" />
                    Logs Detallados de Interacciones
                  </>
                )}
                {activeLogView === 'session' && (
                  <>
                    <Search className="w-5 h-5 text-green-600" />
                    Análisis de Sesión Actual
                  </>
                )}
                {activeLogView === 'agentConfig' && (
                  <>
                    <Settings className="w-5 h-5 text-teal-600" />
                    Configuración Completa del Agente
                  </>
                )}
                {activeLogView === 'serverLog' && (
                  <>
                    <Terminal className="w-5 h-5 text-gray-800" />
                    Log del Servidor (Debug Completo)
                  </>
                )}
              </h3>
              
              <div className="flex items-center gap-2">
                {/* Botón copiar JSON */}
                {logData && !logData.error && (
                  <button
                    onClick={copyToClipboard}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
                  >
                    {copied ? (
                      <>
                        <CheckCheck className="w-4 h-4 text-green-600" />
                        <span className="text-green-600">Copiado</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        {activeLogView === 'serverLog' ? 'Copiar Log' : 'Copiar JSON'}
                      </>
                    )}
                  </button>
                )}
                
                <button
                  onClick={() => {
                    setShowLogsModal(false);
                    setLogData(null);
                    setActiveLogView(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Contenido del Modal */}
            <div className="flex-1 overflow-y-auto p-4">
              {isLoadingLogs ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                  <span className="ml-3 text-gray-600">Cargando datos...</span>
                </div>
              ) : logData?.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-900 font-semibold">Error al cargar datos</p>
                  <p className="text-red-700 text-sm mt-1">{logData.error}</p>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono overflow-x-auto">
                    {activeLogView === 'serverLog' 
                      ? (logData?.logs || 'Log vacío') 
                      : JSON.stringify(logData, null, 2)
                    }
                  </pre>
                </div>
              )}
            </div>

            {/* Footer con instrucciones */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              {activeLogView === 'agentConfig' ? (
                <div className="space-y-2">
                  <p className="text-xs text-gray-600 flex items-center gap-2">
                    <span className="font-semibold">🤖 Configuración del Agente:</span>
                    Este JSON contiene TODA la configuración del agente
                  </p>
                  <ul className="text-[10px] text-gray-600 list-disc list-inside space-y-0.5 ml-2">
                    <li>System Prompt completo</li>
                    <li>Configuración de IA (temperature, tokens, etc.)</li>
                    <li>Todas las fases de la State Machine</li>
                    <li>Escenarios configurados</li>
                    <li>Motor de fricción y triggers</li>
                    <li>Sistema de evaluación</li>
                  </ul>
                  <p className="text-xs text-teal-700 font-medium mt-2">
                    💡 Úsalo con Claude/ChatGPT para validar coherencia de parámetros
                  </p>
                </div>
              ) : (
                <p className="text-xs text-gray-600 flex items-center gap-2">
                  <span className="font-semibold">💡 Tip:</span>
                  Copia el JSON completo y úsalo con Claude, ChatGPT u otra IA para análisis detallado
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Placeholder: Sin métricas */}
      {!friction.enabled && interventions.total === 0 && !detectedIntent && (
        <div className="text-center py-6">
          <Activity className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-xs text-gray-500">
            Las métricas aparecerán durante la sesión
          </p>
        </div>
      )}

      </div>
    </div>
  );
};

export default MetricsPanel;
